<template>
  <div class="container app-shell">
    <app-header>
      <section class="header-section">
        <app-brand-logo />
        <section>
          <app-toggle v-model="isDark" variant="theme" @toggled="toggled"></app-toggle>
          <app-dropdown v-model="language" label="name">
            <app-option v-for="item in languages" :key="item" :value="item">{{ item }} </app-option>
          </app-dropdown>
          <app-burger-menu v-if="isMobile"></app-burger-menu>
        </section>
      </section>
    </app-header>
    <section class="app-shell__body">
      <app-sidebar>
        <template #main="{ isShort }">
          <router-link to="/main/characteristic-colors" active-class="active-link">
            <div class="sidebar-item">
              <div class="icon">
                <font-awesome-icon size="xl" :icon="['fas', 'rainbow']" />
              </div>
              <div v-if="!isShort" class="title">{{ t('characteristicColors') }}</div>
            </div>
          </router-link>
          <router-link to="/main/palette-determinant" active-class="active-link">
            <div class="sidebar-item">
              <div class="icon">
                <font-awesome-icon size="xl" :icon="['fas', 'brush']" />
              </div>
              <div v-if="!isShort" class="title">{{ t('paletteDeterminant') }}</div>
            </div>
          </router-link>
          <router-link to="/main/cut-palette-color" active-class="active-link">
            <div class="sidebar-item">
              <div class="icon">
                <font-awesome-icon size="xl" :icon="['fas', 'scissors']" />
              </div>
              <div v-if="!isShort" class="title">{{ t('cutPalette') }}</div>
            </div>
          </router-link>
          <router-link to="/main/my-palette" active-class="active-link">
            <div class="sidebar-item">
              <div class="icon">
                <font-awesome-icon size="xl" :icon="['fas', 'fill-drip']" />
              </div>
              <div v-if="!isShort" class="title">{{ t('myPalette') }}</div>
            </div>
          </router-link>
          <router-link to="/main/palette" active-class="active-link">
            <div class="sidebar-item">
              <div class="icon">
                <font-awesome-icon size="xl" :icon="['fas', 'camera']" />
              </div>
              <div v-if="!isShort" class="title">{{ t('analysisByPhoto') }}</div>
            </div>
          </router-link>
          <router-link to="/main/chroma" active-class="active-link">
            <div class="sidebar-item">
              <div class="icon">
                <font-awesome-icon size="xl" :icon="['fas', 'eye-dropper']" />
              </div>
              <div v-if="!isShort" class="title">{{ t('pickColor') }}</div>
            </div>
          </router-link>

          <router-link to="/main/ai-agent" active-class="active-link">
            <div class="sidebar-item">
              <div class="icon">
                <font-awesome-icon size="xl" :icon="['fas', 'microchip']" />
              </div>
              <div v-if="!isShort" class="title">{{ t('aiAgent') }}</div>
            </div>
          </router-link>

          <router-link to="/main/gallery" active-class="active-link">
            <div class="sidebar-item">
              <div class="icon">
                <font-awesome-icon size="xl" :icon="['fas', 'images']" />
              </div>
              <div v-if="!isShort" class="title">{{ t('gallery') }}</div>
            </div>
          </router-link>

          <router-link to="/main/consultation" active-class="active-link">
            <div class="sidebar-item">
              <div class="icon">
                <font-awesome-icon size="xl" :icon="['fas', 'user-tie']" />
              </div>
              <div v-if="!isShort" class="title">{{ t('consultation') }}</div>
            </div>
          </router-link>
          <a :href="privacyUrl" target="_blank" rel="noopener">
            <div class="sidebar-item">
              <div class="icon">
                <font-awesome-icon size="xl" :icon="['fas', 'shield-halved']" />
              </div>
              <div v-if="!isShort" class="title">{{ t('privacyPolicy') }}</div>
            </div>
          </a>
          <a :href="termsUrl" target="_blank" rel="noopener">
            <div class="sidebar-item">
              <div class="icon">
                <font-awesome-icon size="xl" :icon="['fas', 'file-lines']" />
              </div>
              <div v-if="!isShort" class="title">{{ t('termsOfService') }}</div>
            </div>
          </a>
        </template>

        <template #additional="{ isShort, toggle }">
          <div class="sidebar-item logout" @click="logout">
            <div class="icon">
              <font-awesome-icon size="xl" :icon="['fas', 'right-from-bracket']" />
            </div>
            <div v-if="!isShort" class="title">{{ t('logout') }}</div>
          </div>

          <div v-if="!isMobile" class="sidebar-item" @click="toggle">
            <font-awesome-icon
              size="xl"
              :icon="isShort ? ['fas', 'arrow-left'] : ['fas', 'arrow-right']"
            />
          </div>
        </template>
      </app-sidebar>
      <main class="main app-shell__main">
        <router-view />
      </main>
    </section>
  </div>
</template>

<script lang="ts">
import AppToggle from '@/shared/components/AppToggle.vue';
import AppDropdown from '@/shared/components/dropdown/AppDropdown.vue';
import AppOption from '@/shared/components/dropdown/AppOption.vue';
import AppHeader from '@/shared/components/AppHeader.vue';
import { computed, defineComponent, ref, watch } from 'vue';
import { useStore } from 'vuex';
import AppSidebar from '@/shared/components/AppSidebar.vue';
import AppBurgerMenu from '@/shared/components/AppBurgerMenu.vue';
import AppBrandLogo from '@/shared/components/AppBrandLogo.vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { privacyPolicyUrl, termsOfServiceUrl } from '@/helpers/privacy-url.helper';

export default defineComponent({
  components: {
    AppBrandLogo,
    AppBurgerMenu,
    AppSidebar,
    AppOption,
    AppDropdown,
    AppHeader,
    AppToggle,
  },
  setup() {
    const { t } = useI18n();
    const store = useStore();
    const router = useRouter();
    const languages = ref(['en', 'ru']);
    const language = ref(store.getters['language/language']);
    const isDark = ref(store.getters['theme/isDark']);
    const isMobile = computed(() => store.getters['mobile/breakPoint'] === 'mobile');
    const privacyUrl = computed(() => privacyPolicyUrl(language.value));
    const termsUrl = computed(() => termsOfServiceUrl(language.value));
    const toggled = (isDark: boolean) => {
      store.dispatch('theme/setTheme', isDark);
    };

    watch(language, (newValue) => {
      store.dispatch('language/setLanguage', { language: newValue });
    });

    const logout = async () => {
      await store.dispatch('authFirebase/logout');
      await store.dispatch('burgerMenu/setBurger', { isOpen: false });
      await router.push('/signIn');
    };

    return {
      toggled,
      logout,
      t,
      isDark,
      language,
      languages,
      isMobile,
      privacyUrl,
      termsUrl,
    };
  },
});
</script>

<style scoped lang="scss">
.container {
  & .header-section {
    display: flex;
    align-items: center;
    justify-content: space-between;

    & section {
      display: flex;
      align-items: center;
      gap: 14px;
    }
  }

  .main {
    width: 100%;
  }
}
</style>
